import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './RFTerminal.css';
import { FaBoxes, FaClipboardList, FaSignOutAlt, FaTerminal, FaUserCircle, FaWarehouse } from 'react-icons/fa';
import config from '../config/config';
import { apiService } from '../services/api.service';

const RFReceiveOrder = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    receivingPoint: '',
    inboundLoad: '',
    license: '',
    orderNumber: '',
    itemNumber: '',
    cases: '',
    caseQty: ''
  });
  


  const validateOrderAndLoad = async (inboundLoad, orderNumber) => {
    try {
      setLoading(true);
      setError('');

      const endpoint = `${config.API_ENDPOINTS.ORDERS.VALIDATE}/${inboundLoad}/${orderNumber}`;
      const response = await apiService.get(endpoint);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Validation failed');
      }

      return result.success;
    } catch (error) {
      
      console.error('Validation error:', error);
      setError('Wrong Order Number or Load Number');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReceiving = async (submissionData) => {
    try {
      setLoading(true);
      
      const response = await apiService.post(
        config.API_ENDPOINTS.ORDERS.RECEIVE,
        submissionData
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit receiving');
      }

      return true;
    } catch (error) {
      console.error('Error submitting receive order:', error);
      setError(error.toString());
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = useCallback((event) => {
    // If there's an error, any key press should clear it
    // if (error) {
    //   setError('');
    //   return;
    // }

    const key = event.key.toUpperCase();
    console.log('Key pressed:', key);

    // Handle numeric input
    if (/^[0-9]$/.test(key)) {
      setInput(prev => prev + key);
      return;
    }

    // Handle special keys
    switch (key) {
      case 'ENTER':
        handleEnter();
         if (error) {
      setError('');
      return;
    }
        break;
      
      case 'BACKSPACE':
        setInput(prev => prev.slice(0, -1));
        break;
      
      case 'F1':
        if (step > 1) {
          setStep(prev => prev - 1);
          setInput('');
          setError('');
        } else {
          navigate('/rf');
        }
        break;
    }
  }, [step, input, error, navigate]);

  const handleEnter = async () => {
    if (!input && step !== 8) {
      setError('Input required');
      return;
    }

    try {
      switch (step) {
        case 1:
          setData(prev => ({ ...prev, receivingPoint: input }));
          setInput('');
          setStep(2);
          break;

        case 2:
          setData(prev => ({ ...prev, inboundLoad: input }));
          setInput('');
          setStep(3);
          break;

        case 3:
          setData(prev => ({ ...prev, license: input }));
          setInput('');
          setStep(4);
          break;

        case 4:
          const isValid = await validateOrderAndLoad(data.inboundLoad, input);
          if (isValid) {
            setData(prev => ({ ...prev, orderNumber: input }));
            setInput('');
            setStep(5);
          }
          break;

        case 5:
          setData(prev => ({ ...prev, itemNumber: input }));
          setInput('');
          setStep(6);
          break;

        case 6:
          if (isNaN(input) || Number(input) <= 0) {
            setError('Please enter a valid number of cases');
            return;
          }
          setData(prev => ({ ...prev, cases: input }));
          setInput('');
          setStep(7);
          break;

        case 7:
          if (isNaN(input) || Number(input) <= 0) {
            setError('Please enter a valid case quantity');
            return;
          }

          // Create updated data object with current input as caseQty
          const updatedData = {
            ...data,
            caseQty: input
          };

          // Submit with the complete data
          const success = await handleSubmitReceiving(updatedData);
          
          if (success) {
            // Update state after successful submission
            setData(updatedData);
            setInput('');
            setStep(8);
          }
          break;

        case 8:
          // Reset and return to menu
          setStep(1);
          setInput('');
          setData({
            receivingPoint: '',
            inboundLoad: '',
            license: '',
            orderNumber: '',
            itemNumber: '',
            cases: '',
            caseQty: ''
          });
          navigate('/rf');
          return;
      }

      // setError('');
    } catch (error) {
      console.error('Error in handleEnter:', error);
      setError(error.message || 'System Error');
    }
  };

  // Set up keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // Debug logging
  useEffect(() => {
    console.log('Current State:', { step, input, data, error });
  }, [step, input, data, error]);

  // Debug logging for state changes
  useEffect(() => {
    console.log('Current data state:', data);
    console.log('Current input:', input);
    console.log('Current step:', step);
  }, [data, input, step]);

  const renderScreen = () => {
    const screens = {
      1: (
        <div className="rf-screen">
          <div className="rf-title">RECEIVE ORDER</div>
          <div className="rf-prompt">ENTER RECEIVING POINT</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
        </div>
      ),
      2: (
        <div className="rf-screen">
          <div className="rf-title">RECEIVE ORDER</div>
          <div className="rf-info">RECV PT: {data.receivingPoint}</div>
          <div className="rf-prompt">ENTER INBOUND LOAD #</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
        </div>
      ),
      3: (
        <div className="rf-screen">
          <div className="rf-title">RECEIVE ORDER</div>
          <div className="rf-info">LOAD: {data.inboundLoad}</div>
          <div className="rf-prompt">ENTER LICENSE #</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
        </div>
      ),
      4: (
        <div className="rf-screen">
          <div className="rf-title">RECEIVE ORDER</div>
          <div className="rf-info">LICENSE: {data.license}</div>
          <div className="rf-info">LOAD: {data.inboundLoad}</div>
          <div className="rf-prompt">ENTER ORDER #</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
          {loading && <div className="rf-loading">Validating...</div>}
        </div>
      ),
      5: (
        <div className="rf-screen">
          <div className="rf-title">RECEIVE ORDER</div>
          <div className="rf-info">ORDER: {data.orderNumber}</div>
          <div className="rf-prompt">ENTER ITEM #</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
        </div>
      ),
      6: (
        <div className="rf-screen">
          <div className="rf-title">RECEIVE ORDER</div>
          <div className="rf-info">ITEM: {data.itemNumber}</div>
          <div className="rf-prompt">ENTER # OF CASES</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
        </div>
      ),
      7: (
        <div className="rf-screen">
          <div className="rf-title">RECEIVE ORDER</div>
          <div className="rf-info">CASES: {data.cases}</div>
          <div className="rf-prompt">ENTER CASE QTY</div>
          <div className="rf-input-line">
            <span className="rf-input-label">&gt;</span>
            <span className="rf-input-value">{input}</span>
            <span className="rf-input-cursor">_</span>
          </div>
          {loading && <div className="rf-loading">Processing...</div>}
        </div>
      ),
      8: (
        <div className="rf-screen">
          <div className="rf-title">RECEIVE COMPLETE</div>
          <div className="rf-info">ORDER: {data.orderNumber}</div>
          <div className="rf-info">CASES: {data.cases}</div>
          <div className="rf-info">CASE QTY: {data.caseQty}</div>
          <div className="rf-info">TOTAL QTY: {Number(data.cases) * Number(data.caseQty)}</div>
          <div className="rf-prompt">PRESS ENTER TO CONTINUE</div>
        </div>
      )
    };

    return screens[step];
  };

  return (


    <div className="inbound-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <FaWarehouse className="sidebar-icon" />
          <span>WMS System</span>
        </div>
        <nav className="sidebar-nav">
          <Link to="/inbound-order" className="active"><FaBoxes /> Inbound Orders</Link>
          <Link to="/rf" className="active"><FaTerminal /> RF Terminal</Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Navigation */}
        <header className="top-nav">
          <div className="search-container">
            <input 
              type="text"
              placeholder="Search..."
              className="search-input"
            />
          </div>
          <div className="user-nav">
            <span className="user-name">
              <FaUserCircle /> John Doe
            </span>
            <button onClick={() => navigate('/login')} className="logout-btn">
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="page-content">
          <div className="content-header">
            <h1>RF Receive Order</h1>
          </div>
    <div className="rf-terminal" tabIndex={0}>

      {renderScreen()}
      {error && (
        <div className="rf-error-message">
          {error}
          <div className="rf-error-prompt">Press Enter Key to Continue</div>
        </div>
      )}
      <div className="rf-footer">
        <div>F1:BACK</div>
        <div>ENT:NEXT</div>
      </div>
    </div>
    </div>
    </div>
    </div>
  );
};

export default RFReceiveOrder; 